import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import { useFeature } from 'flagged';
import {
  DialogActionButton,
  DialogActions,
  DialogCancelButton,
  DialogHeader,
} from '@/components/common/CustomDialog';
import ControlledTextField from '@/components/common/form/ControlledTextField';
import FeatureFlags from '@/constants/system/FeatureFlags';
import translate from '@/lib/intl/translate';
import { useQueryProvider } from '@/modules/QueryProvider';
import useSavedFilters, {
  type AdvancedSearchUserQueryType,
} from '../hooks/useSavedFilters';
import type ConstituentSearchFilterParams from '../types/ConstituentSearchFilterParams';

const FILTER_NAME = 'filterName';
const defaultValues = { [FILTER_NAME]: '' };

function SaveFilterPrompt({
  className = '',
  disabled = false,
}: {
  className?: string;
  disabled?: boolean;
}) {
  const hasColumnSelection = useFeature(
    FeatureFlags.CONSTITUENT_COLUMN_SELECTION,
  );
  const { query, updateQuery } =
    useQueryProvider<ConstituentSearchFilterParams>();
  const { addFilter } = useSavedFilters();
  const [isDialogActive, setIsDialogActive] = React.useState(false);

  const formMethods = useForm({ defaultValues });
  const { control, formState, handleSubmit, reset, setError } = formMethods;

  const closeDialog = () => {
    setIsDialogActive(false);
    reset(defaultValues);
  };

  const onSubmit = React.useCallback(
    (e: React.FormEvent) =>
      handleSubmit(async (formData) => {
        try {
          const { filters, advancedFilters, columns, customFieldColumns } =
            query;

          const filterObj: AdvancedSearchUserQueryType = {};
          if (filters) filterObj.filters = filters;
          if (advancedFilters) filterObj.advancedFilters = advancedFilters;
          if (hasColumnSelection) {
            if (columns) filterObj.columns = columns;
            if (customFieldColumns)
              filterObj.customFieldColumns = customFieldColumns;
          }

          await addFilter(formData[FILTER_NAME], filterObj);
          updateQuery({ filterName: formData[FILTER_NAME] });
          closeDialog();
        } catch (err) {
          const message =
            err instanceof Error ? err.message : translate('API_ERROR_UNKNOWN');
          setError(FILTER_NAME, { type: 'server', message });
        }
      })(e),
    [addFilter, handleSubmit, query],
  );

  return (
    <>
      <Button
        className={className}
        variant="text"
        type="button"
        onClick={() => setIsDialogActive(true)}
        disabled={disabled}
        data-testid="filters-create-saved-button"
      >
        {translate('CONSTITUENT_FILTER_SAVE_FILTER')}
      </Button>
      <Dialog onClose={closeDialog} open={isDialogActive}>
        <DialogHeader
          headerText={translate('CONSTITUENT_FILTER_SAVE_FILTER')}
          onClose={closeDialog}
        />
        <FormProvider {...formMethods}>
          <form noValidate onSubmit={onSubmit}>
            <DialogContent dividers>
              <div className="mb-6 type-body1">
                {translate('CONSTITUENT_FILTER_SAVE_MODAL')}
              </div>
              <ControlledTextField
                className="w-full"
                control={control}
                error={!!formState.errors[FILTER_NAME]}
                helperText={formState.errors[FILTER_NAME]?.message}
                name={FILTER_NAME}
                label={translate('CONSTITUENT_FILTER_NAME')}
                rules={{ required: translate('FORMS_VALIDATION_REQUIRED') }}
                required
              />
            </DialogContent>
            <DialogActions>
              <DialogActionButton
                showLoader={formState.isSubmitting}
                type="submit"
              >
                {translate('SAVE')}
              </DialogActionButton>
              <DialogCancelButton onClick={closeDialog} />
            </DialogActions>
          </form>
        </FormProvider>
      </Dialog>
    </>
  );
}

export default SaveFilterPrompt;
